
import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Search, FileText } from "lucide-react";
import { TemplateCategory, TemplateSubcategory, Template } from "@/data/templates";

interface TemplatesViewProps {
  category: TemplateCategory;
  subcategory: TemplateSubcategory;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  onBack: () => void;
  onSelectTemplate: (template: Template) => void;
}

const TemplatesView = ({ 
  category, 
  subcategory, 
  searchTerm, 
  setSearchTerm, 
  onBack, 
  onSelectTemplate 
}: TemplatesViewProps) => {
  // Filtrar templates baseado na busca
  const filterTemplates = (templates: Template[]) => {
    if (!searchTerm) return templates;
    return templates.filter(template => 
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.type.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredTemplates = filterTemplates(subcategory.templates);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 mb-6">
        <Button 
          variant="outline" 
          onClick={onBack}
          className="sidebar-button-no-hover gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold flex items-center gap-2 text-indigo-900">
            <span className="text-xl">{subcategory.emoji}</span>
            {subcategory.name}
          </h2>
          <p className="text-purple-800 font-medium">
            {category.name} • {filteredTemplates.length} modelos
          </p>
        </div>
      </div>

      {/* Campo de busca */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          placeholder="Buscar modelos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white/10 border-white/30 text-indigo-900 placeholder:text-indigo-700 focus:border-white/60"
        />
      </div>

      {/* Lista de templates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-8 overflow-y-auto max-h-[calc(100vh-240px)] pr-2">
        {filteredTemplates.map((template) => (
          <Card 
            key={template.id}
            className="document-template-card cursor-pointer"
            onClick={() => onSelectTemplate(template)}
          >
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2 text-indigo-900">
                <FileText className="h-4 w-4 text-blue-600" />
                {template.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm mb-3 text-gray-600">
                {template.description}
              </p>
              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                {template.type}
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTemplates.length === 0 && searchTerm && (
        <div className="text-center py-8 text-purple-800 font-medium">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Nenhum modelo encontrado para "{searchTerm}"</p>
        </div>
      )}
    </div>
  );
};

export default TemplatesView;
